import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { TableFilterBar } from './ui/TableFilterBar';
import { TablePeriodSelector } from './ui/TablePeriodSelector';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Combobox } from './ui/combobox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowDownCircle, Shield, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { useCuotasLoteIndividual } from '../hooks/useCuotasLote';
import { aprobarDevolucionConImpacto } from '../hooks/useDevoluciones';
import { DownloadReceiptButton } from './ui/DownloadReceiptButton';
import { DetalleDevolucion } from '../utils/receiptTemplates';

const PORCENTAJE_PENALIZACION = 0.2; // 20% fijo

export default function Devoluciones() {
  const currentYear = new Date().getFullYear();
  const [selectedMonth, setSelectedMonth] = useState('Todos');
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [modalGenerarOpen, setModalGenerarOpen] = useState(false);
  const [modalAplicarOpen, setModalAplicarOpen] = useState(false);
  const { devoluciones, loading, lots, addDevolucion, aprobarDevolucion, config } = useAppData();

  // --- Estado del formulario de devolución ---
  const [loteSeleccionado, setLoteSeleccionado] = useState('');
  const [fecha, setFecha] = useState(getTodayYMD());
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para modal de aprobación
  const [modalAprobarOpen, setModalAprobarOpen] = useState(false);
  const [devolucionAprobar, setDevolucionAprobar] = useState<any>(null);
  const [aprobacionForm, setAprobacionForm] = useState({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '', banco: '', beneficiario: '' }); // boleta oculto en UI pero mantenido para compatibilidad
  const [aprobando, setAprobando] = useState(false);
  const [aprobacionError, setAprobacionError] = useState<string | null>(null);
  const [aprobacionSuccess, setAprobacionSuccess] = useState<string | null>(null);

  // Función pura para calcular saldo pendiente de un lote a crédito
  function calcularSaldoPendiente(lote: any): number {
    const totalValue = lote.totalValue || parseFloat(lote.valorTotal) || 0;
    const payments = Array.isArray(lote.payments) ? lote.payments : [];
    const pagado = payments
      .filter((p: any) => p.type === 'abono' || p.type === 'enganche')
      .reduce((sum: number, p: any) => sum + (parseFloat(p.amount) || 0), 0);
    return totalValue - pagado;
  }
  // Lotes a crédito (sin devolución pendiente/aprobada)
  const lotesNoElegibles = new Set(
    devoluciones
      .filter((d: any) => d.estado === 'pendiente' || d.estado === 'aprobada')
      .flatMap((d: any) => Array.isArray(d.lotes) ? d.lotes : [d.lotes])
  );
  const lotesCredito = lots.filter((l: any) =>
    (l.paymentType === 'pagos' || l.status === 'crédito') &&
    !lotesNoElegibles.has(l.id)
  );
  // Filtrar solo los que tienen saldo pendiente > 0
  const lotesElegibles = lotesCredito.filter((l: any) => calcularSaldoPendiente(l) > 0);
  const opcionesLotes = lotesElegibles.map((l: any) => ({
    label: `Lote ${l.id} – ${l.buyerName || l.comprador || ''}`,
    value: l.id
  }));
  // Al seleccionar un lote, usar el hook para mostrar detalles
  const loteData = lots.find((l: any) => l.id === loteSeleccionado);
  const { saldoPendiente } = useCuotasLoteIndividual(loteData);
  const montoPagado = loteData?.amountPaid || 0;
  const penalizacion = Math.round((loteData?.lotValue || 15000) * PORCENTAJE_PENALIZACION);
  const montoDevuelto = Math.max(montoPagado - penalizacion, 0);
  const vendedor = loteData?.seller || loteData?.vendedor || '';
  const comprador = loteData?.buyerName || loteData?.comprador || '';

  // Eliminar la declaración duplicada de loteInputValue y setLoteInputValue
  const [loteInputValue, setLoteInputValue] = useState('');

  // Filtrar opciones según búsqueda
  const opcionesLotesFiltradas = opcionesLotes.filter((opt: { label: string; value: string }) => {
    const search = loteInputValue.toLowerCase();
    return (
      opt.label.toLowerCase().includes(search) ||
      opt.value.toLowerCase().includes(search)
    );
  });

  // Utilidad para obtener la fecha de hoy en formato YYYY-MM-DD
  function getTodayYMD() {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split('T')[0];
  }

  // Handler para abrir modal de aprobación
  const handleAbrirAprobar = (devolucion: any) => {
    setDevolucionAprobar(devolucion);
    setAprobacionForm({ numeroDocumento: '', transferencia: '', boleta: '', fecha: '', banco: '', beneficiario: '' });
    setAprobacionError(null);
    setAprobacionSuccess(null);
    setModalAprobarOpen(true);
  };

  // Handler para aprobar devolución
  const handleAprobar = async () => {
    setAprobacionError(null);
    setAprobacionSuccess(null);
    if (!aprobacionForm.numeroDocumento || !aprobacionForm.fecha || !aprobacionForm.banco || !aprobacionForm.beneficiario) {
      setAprobacionError('El No. Doc. del Banco, fecha, banco y beneficiario son obligatorios.');
      return;
    }
    setAprobando(true);
    try {
      await aprobarDevolucionConImpacto(
        devolucionAprobar,
        'admin', // TODO: usuario actual
        {
          numeroDocumento: aprobacionForm.numeroDocumento,
          transferencia: aprobacionForm.transferencia,
          boleta: aprobacionForm.boleta,
          fecha: aprobacionForm.fecha,
          banco: aprobacionForm.banco,
          beneficiario: aprobacionForm.beneficiario
        }
      );
      setAprobacionSuccess('Devolución aprobada correctamente.');
      setTimeout(() => setModalAprobarOpen(false), 1200);
    } catch (e) {
      setAprobacionError('Error al aprobar la devolución.');
    } finally {
      setAprobando(false);
    }
  };

  // Cálculo de métricas
  const totalDevuelto = devoluciones
    .filter((d: any) => d.estado === 'aprobada')
    .reduce((sum: number, d: any) => sum + (d.montoDevuelto || 0), 0);
  const totalRetenido = devoluciones
    .filter((d: any) => d.estado === 'aprobada')
    .reduce((sum: number, d: any) => sum + (d.penalizacion || 0), 0);

  // Handler para generar devolución
  const handleGenerarDevolucion = async () => {
    setError(null);
    setSuccess(null);
    if (!loteSeleccionado || !fecha || !motivo) {
      setError('Completa todos los campos.');
      return;
    }
    if (!loteData) {
      setError('Selecciona un lote válido.');
      return;
    }
    if (montoPagado <= 0) {
      setError('El lote seleccionado no tiene pagos registrados.');
      return;
    }
    setIsSubmitting(true);
    try {
      await addDevolucion({
        lotes: [loteSeleccionado],
        comprador,
        vendedor,
        fechaSolicitud: fecha,
        motivo,
        montoPagado,
        penalizacion,
        porcentajePenalizacion: PORCENTAJE_PENALIZACION * 100,
        montoDevuelto,
        usuarioSolicita: 'admin', // TODO: reemplazar por usuario actual
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setSuccess('Devolución registrada correctamente.');
      setLoteSeleccionado('');
      setFecha('');
      setMotivo('');
      setTimeout(() => setModalGenerarOpen(false), 1200);
    } catch (e: any) {
      setError('Error al registrar la devolución.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header y botón de acción */}
      <div className="flex justify-between items-center mb-2">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Devoluciones</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Gestiona y visualiza todas las devoluciones de lotes en el sistema</p>
        </div>
        <Button onClick={() => setModalGenerarOpen(true)} className="bg-transparent border border-input hover:bg-accent text-foreground rounded-[6px] h-8 text-[13px] font-medium transition-colors px-3">
          <span className="text-lg font-bold mr-1.5">+</span> Realizar devolución
        </Button>
      </div>

      {/* Selector de período de consulta */}
      <TablePeriodSelector
        selectedMonth={selectedMonth}
        selectedYear={selectedYear}
        onMonthChange={setSelectedMonth}
        onYearChange={setSelectedYear}
      />

      {/* Cards métricas estilo vendedores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-[13px] font-medium text-muted-foreground">Total devuelto</CardTitle>
            <ArrowDownCircle className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-500">Q. {totalDevuelto.toLocaleString()}</div>
            <p className="text-[11px] text-muted-foreground mt-1">Total de dinero devuelto a compradores</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-amber-500/20 shadow-none rounded-lg overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-[13px] font-medium text-muted-foreground">Total retenido</CardTitle>
            <Shield className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-500">Q. {totalRetenido.toLocaleString()}</div>
            <p className="text-[11px] text-muted-foreground mt-1">Total retenido por penalización</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de devoluciones */}
      <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
        {loading ? (
          <div className="text-center text-muted-foreground py-12">Cargando devoluciones...</div>
        ) : devoluciones.length === 0 ? (
          <div className="text-center text-muted-foreground py-12">No hay devoluciones registradas</div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="min-w-full text-sm">
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-white/5">
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Lote(s)</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Comprador</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Vendedor</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Motivo</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Penalización</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Devuelto</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Estado</TableHead>
                  <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devoluciones.map((d: any) => (
                  <TableRow key={d.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                    <TableCell className="px-3 py-3 text-[13px]">{d.fechaSolicitud ? new Date(d.fechaSolicitud).toLocaleDateString('es-GT') : '-'}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[13px]">{Array.isArray(d.lotes) ? d.lotes.join(', ') : d.lotes}</TableCell>
                    <TableCell className="px-3 py-3 text-[13px]">{d.comprador}</TableCell>
                    <TableCell className="px-3 py-3 text-[13px]">{d.vendedor}</TableCell>
                    <TableCell className="px-3 py-3 text-[13px]">{d.motivo}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[13px] text-amber-500">Q. {d.penalizacion?.toLocaleString()}</TableCell>
                    <TableCell className="px-3 py-3 font-mono text-[13px] text-emerald-500">Q. {d.montoDevuelto?.toLocaleString()}</TableCell>
                    <TableCell className="px-3 py-3">
                      {d.estado === 'aprobada' ? (
                        <div className="flex items-center gap-1.5 text-emerald-500">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span className="text-[13px] font-medium">Aprobada</span>
                        </div>
                      ) : d.estado === 'pendiente' ? (
                        <div className="flex items-center gap-1.5 text-amber-500">
                          <Clock className="h-3.5 w-3.5" />
                          <span className="text-[13px] font-medium">Pendiente</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-rose-500">
                          <AlertCircle className="h-3.5 w-3.5" />
                          <span className="text-[13px] font-medium">{d.estado.charAt(0).toUpperCase() + d.estado.slice(1)}</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="px-3 py-3">
                      <div className="flex gap-2">
                        {/* Botón de descarga de recibo - solo si está aprobada */}
                        {d.estado === 'aprobada' && (
                          <DownloadReceiptButton
                            empresaInfo={{
                              razonSocial: config.razonSocial || 'Bimobi',
                              nit: config.nit,
                              direccionCompleta: config.direccionCompleta || config.ubicacion || 'Guatemala',
                              telefono1: config.telefono1 || '',
                              telefono2: config.telefono2,
                              email: config.email,
                              logoUrl: config.logoUrl
                            }}
                            detalleRecibo={{
                              tipoRecibo: 'devolucion',
                              cliente: {
                                nombre: d.comprador || 'Sin nombre'
                              },
                              lotes: Array.isArray(d.lotes) ? d.lotes.join(', ') : d.lotes || '-',
                              vendedor: d.vendedor || '-',
                              fechaSolicitud: d.fechaSolicitud || '',
                              motivo: d.motivo || '',
                              montoPagado: d.montoPagado || 0,
                              penalizacion: d.penalizacion || 0,
                              porcentajePenalizacion: d.porcentajePenalizacion || 20,
                              montoDevuelto: d.montoDevuelto || 0,
                              banco: d.datosTransferencia?.banco || '-',
                              beneficiario: d.datosTransferencia?.beneficiario || d.comprador || '-',
                              boletaTransferencia: d.datosTransferencia?.boleta || '-'
                            } as Omit<DetalleDevolucion, 'numeroRecibo' | 'fechaEmision'>}
                            fileName={`Recibo-Devolucion-${Array.isArray(d.lotes) ? d.lotes.join('-') : d.lotes}`}
                            onSuccess={(numeroRecibo) => {
                              console.log('Recibo de devolución generado:', numeroRecibo);
                            }}
                          />
                        )}

                        {/* Botón de aplicar - solo si está pendiente */}
                        {d.estado === 'pendiente' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAbrirAprobar(d)}
                          >
                            Aplicar devolución
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Modal para generar devolución */}
      <Dialog open={modalGenerarOpen} onOpenChange={(open: boolean) => {
        setModalGenerarOpen(open);
        if (!open) {
          setLoteSeleccionado('');
          setFecha('');
          setMotivo('');
          setError(null);
          setSuccess(null);
        }
      }}>
        <DialogContent className="max-w-md bg-white border border-gray-200 rounded-xl">
          <DialogHeader>
            <DialogTitle>Generar devolución</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Lote</label>
              <Combobox
                placeholder="Buscar lote..."
                value={loteSeleccionado}
                onValueChange={setLoteSeleccionado}
                options={opcionesLotesFiltradas}
                inputValue={loteInputValue}
                onInputChange={setLoteInputValue}
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Fecha</label>
              <Input type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Motivo</label>
              <Input placeholder="Motivo de la devolución" value={motivo} onChange={e => setMotivo(e.target.value)} />
            </div>
            {loteSeleccionado && loteData && (
              (() => {
                // Detectar si es grupal (ID con guion y existe en lots)
                const isGrupo = loteData.id && loteData.id.includes('-') && lots.find((x: any) => x.id === loteData.id);
                let lotesInfo;
                let penalizacionTotal = 0;
                let valorTotalLotes = 0;
                let montoAbonadoTotal = 0;
                let montoDevueltoTotal = 0;
                if (isGrupo) {
                  // Tomar datos del documento grupal
                  const grupo = lots.find((x: any) => x.id === loteData.id);
                  lotesInfo = [{
                    id: grupo.id,
                    lotValue: grupo.lotValue || 15000,
                    amountPaid: grupo.amountPaid || 0,
                    buyerName: grupo.buyerName || grupo.comprador || '-',
                    buyerDPI: grupo.buyerDPI || '',
                    phoneNumber: grupo.phoneNumber || '',
                  }];
                  valorTotalLotes = grupo.totalValue || grupo.lotValue || 15000;
                  penalizacionTotal = Math.round(valorTotalLotes * PORCENTAJE_PENALIZACION);
                  montoAbonadoTotal = grupo.amountPaid || 0;
                  montoDevueltoTotal = Math.max(montoAbonadoTotal - penalizacionTotal, 0);
                } else {
                  // Individual o grupo no encontrado: usar individuales
                  const lotesIds = Array.isArray(loteData.idLotes) && loteData.idLotes.length > 1
                    ? loteData.idLotes
                    : [loteData.id];
                  lotesInfo = lotesIds.map((id: string) => {
                    const l = lots.find((x: any) => x.id === id);
                    return {
                      id,
                      lotValue: l?.lotValue || 15000,
                      amountPaid: l?.amountPaid || 0,
                      buyerName: l?.buyerName || l?.comprador || '-',
                      buyerDPI: l?.buyerDPI || '',
                      phoneNumber: l?.phoneNumber || '',
                    };
                  });
                  valorTotalLotes = lotesInfo.reduce((sum: any, l: any) => sum + l.lotValue, 0);
                  penalizacionTotal = lotesInfo.reduce((sum: any, l: any) => sum + Math.round(l.lotValue * PORCENTAJE_PENALIZACION), 0);
                  montoAbonadoTotal = lotesInfo.reduce((sum: any, l: any) => sum + l.amountPaid, 0);
                  montoDevueltoTotal = Math.max(montoAbonadoTotal - penalizacionTotal, 0);
                }
                return (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                    <div><b>Número de lote{lotesInfo.length > 1 ? 's' : ''}:</b> {lotesInfo.map((l: any) => l.id).join(', ')}</div>
                    <div><b>Comprador:</b> {lotesInfo[0].buyerName}</div>
                    {lotesInfo[0].buyerDPI && <div><b>DPI:</b> {lotesInfo[0].buyerDPI}</div>}
                    {lotesInfo[0].phoneNumber && <div><b>Teléfono:</b> {lotesInfo[0].phoneNumber}</div>}
                    <div><b>Cantidad de lotes:</b> {lotesInfo.length}</div>
                    <div><b>Valor total del lote{lotesInfo.length > 1 ? 's' : ''}:</b> Q. {valorTotalLotes.toLocaleString()}</div>
                    <div><b>Penalización (20%):</b> Q. {penalizacionTotal.toLocaleString()}</div>
                    <div><b>Total abonado a la fecha:</b> Q. {montoAbonadoTotal.toLocaleString()}</div>
                    <div><b>Monto a devolver:</b> <span className="text-green-700 font-bold">Q. {montoDevueltoTotal.toLocaleString()}</span></div>
                    {montoDevueltoTotal <= 0 && (
                      <div className="text-xs text-yellow-700 mt-1">El monto abonado es menor o igual a la penalización. No hay devolución a entregar.</div>
                    )}
                    {lotesInfo.length > 1 && !isGrupo && (
                      <div className="mt-2 text-xs text-gray-600">
                        <b>Desglose por lote:</b>
                        <ul className="list-disc ml-5">
                          {lotesInfo.map((l: any, idx: any) => (
                            <li key={l.id}>
                              Lote {l.id}: Abonado Q. {l.amountPaid.toLocaleString()} - Penalización Q. {Math.round(l.lotValue * PORCENTAJE_PENALIZACION).toLocaleString()}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
            {error && <div className="text-red-600 text-sm">{error}</div>}
            {success && <div className="text-green-600 text-sm">{success}</div>}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setModalGenerarOpen(false)} className="border-gray-200 hover:bg-gray-50">Cancelar</Button>
              <Button className="bg-black text-white hover:bg-gray-900" onClick={handleGenerarDevolucion} disabled={isSubmitting}>
                {isSubmitting ? 'Generando...' : 'Generar devolución'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para aplicar devolución (placeholder) */}
      <Dialog open={modalAplicarOpen} onOpenChange={setModalAplicarOpen}>
        <DialogContent className="max-w-md bg-white border border-gray-200 rounded-xl">
          <DialogHeader>
            <DialogTitle>Aplicar devolución</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Boleta de reintegro</label>
              <Input placeholder="Número de boleta" />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Fecha de transferencia</label>
              <Input type="date" />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Banco / Beneficiario</label>
              <Input placeholder="Banco y nombre del beneficiario" />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setModalAplicarOpen(false)} className="border-gray-200 hover:bg-gray-50">Cancelar</Button>
              <Button className="bg-black text-white hover:bg-gray-900">Aplicar devolución</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para aprobar devolución */}
      <Dialog open={modalAprobarOpen} onOpenChange={setModalAprobarOpen}>
        <DialogContent className="max-w-md bg-white border border-gray-200 rounded-xl">
          <DialogHeader>
            <DialogTitle>Aprobar devolución</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                No. Doc. del Banco <span className="text-red-500">*</span>
                <span className="text-xs text-gray-500 block mt-1 font-normal">Número de documento que aparece en el estado de cuenta del banco</span>
              </label>
              <Input placeholder="Ej. 9186345" value={aprobacionForm.numeroDocumento} onChange={e => setAprobacionForm(f => ({ ...f, numeroDocumento: e.target.value.replace(/\D/g, '') }))} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                No. Autorización <span className="text-gray-400">(Opcional)</span>
                <span className="text-xs text-gray-500 block mt-1 font-normal">Número de autorización o transferencia (diferente al No. Doc. del banco)</span>
              </label>
              <Input placeholder="Ej. 123456789" value={aprobacionForm.transferencia} onChange={e => setAprobacionForm(f => ({ ...f, transferencia: e.target.value.replace(/\D/g, '') }))} />
            </div>
            {/* Campo boleta oculto pero mantenido en estado para compatibilidad histórica */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Fecha de transferencia <span className="text-red-500">*</span></label>
              <Input type="date" value={aprobacionForm.fecha} onChange={e => setAprobacionForm(f => ({ ...f, fecha: e.target.value }))} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Banco <span className="text-red-500">*</span></label>
              <Input placeholder="Banco" value={aprobacionForm.banco} onChange={e => setAprobacionForm(f => ({ ...f, banco: e.target.value }))} />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Beneficiario <span className="text-red-500">*</span></label>
              <Input placeholder="Nombre del beneficiario" value={aprobacionForm.beneficiario} onChange={e => setAprobacionForm(f => ({ ...f, beneficiario: e.target.value }))} />
            </div>
            {aprobacionError && <div className="text-red-600 text-sm">{aprobacionError}</div>}
            {aprobacionSuccess && <div className="text-green-600 text-sm">{aprobacionSuccess}</div>}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setModalAprobarOpen(false)} className="border-gray-200 hover:bg-gray-50">Cancelar</Button>
              <Button className="bg-green-600 text-white hover:bg-green-700" onClick={handleAprobar} disabled={aprobando}>
                {aprobando ? 'Aprobando...' : 'Aprobar devolución'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 